import { useEffect } from "react";

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import { conflictText } from "@/lib/tina/festivals";
import {
  FESTIVAL_QUERY_ROOT,
  useFestivalRepository,
} from "@/pages/tina/festivals/hooks/use-festival-repository";
import type { PageForm } from "@/pages/tina/festivals/lib/page-form";
import {
  type PageMode,
  previewBody,
} from "@/pages/tina/festivals/lib/page-request";
import { festivalPageKey } from "@/pages/tina/festivals/lib/query-keys";
import type {
  FestivalPageRead,
  PreviewBody,
  PreviewLine,
} from "@/types/tina-festival-page";

/** How long the curator pauses before Tina is asked what the page would make. */
export const PREVIEW_DEBOUNCE_MS = 500;

/**
 * Where the preview stands.
 * - `stored`: the form is untouched and no answer has come: the page read's own preview, so the first paint is never empty.
 * - `waiting`: the newest body's answer is on its way; the older answer shows dimmed, never as current.
 * - `fresh`: this is Tina's answer to the form as it is now.
 * - `blocked`: the body cannot be sent (a shape problem), so Tina is not asked.
 * - `error`: Tina could not answer.
 */
export type PreviewState = "stored" | "waiting" | "fresh" | "blocked" | "error";

export interface PagePreview {
  lines: PreviewLine[];
  problems: string[];
  state: PreviewState;
  errorText: string | null;
}

const GENERIC_ERROR =
  "Tina could not show the preview. Approve and publish still checks everything.";

const failureOf = (error: unknown): { status?: number; message?: string } => {
  if (typeof error !== "object" || error === null) return {};
  const { statusCode, message } = error as {
    statusCode?: unknown;
    message?: unknown;
  };
  return {
    status: typeof statusCode === "number" ? statusCode : undefined,
    message: typeof message === "string" ? message : undefined,
  };
};

/** A 409 that says the page is not what the body was made for: read the page again. */
const isStaleAnswer = (status?: number, message?: string): boolean =>
  status === 409 &&
  message !== undefined &&
  (message === "skeleton_changed" ||
    message === "page_changed" ||
    message.startsWith("record_state"));

/** The sentence for a failed preview. */
function errorTextOf(status?: number, message?: string): string {
  if (
    status === 409 &&
    message !== undefined &&
    (message === "publishing" || message.startsWith("held_by_other_record"))
  ) {
    return conflictText(message);
  }
  return GENERIC_ERROR;
}

interface PagePreviewInput {
  page: FestivalPageRead;
  form: PageForm | null;
  dirty: boolean;
  mode: PageMode | null;
  /** A write is on its way: no question is asked meanwhile. */
  busy: boolean;
}

/**
 * Tina's dry run of the body Approve or Publish changes would send, asked 500 ms after the last edit. IMS holds no
 * copy of the preview texts. An untouched form asks nothing: the page's own stored preview is Tina's answer for it. The query key carries the body, so an answer is only ever current for the form it was
 * asked for: while the newest body waits, the older answer is a placeholder, shown dimmed, never as current.
 */
export function usePagePreview({
  page,
  form,
  dirty,
  mode,
  busy,
}: PagePreviewInput): PagePreview {
  const repo = useFestivalRepository();
  const queryClient = useQueryClient();

  const asks = mode === "approve" || mode === "changes";
  const body: PreviewBody | null =
    asks && form !== null ? previewBody(form, page) : null;
  const bodyKey = body === null ? null : JSON.stringify(body);
  // An untouched form asks nothing, so the first edit starts the pause rather than finding an old body asked for.
  const [askedKey] = useDebounce(dirty ? bodyKey : null, PREVIEW_DEBOUNCE_MS);

  const query = useQuery({
    queryKey: [FESTIVAL_QUERY_ROOT, "preview", page.record_id, askedKey],
    queryFn: () =>
      repo.previewPage(
        page.record_id,
        JSON.parse(askedKey ?? "null") as PreviewBody,
      ),
    enabled:
      asks &&
      askedKey !== null &&
      repo.resolvedBy !== undefined &&
      !busy &&
      page.status !== "publishing",
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
    // A preview is a question about this form only: it is not kept for another visit.
    gcTime: 0,
  });

  const failure = query.isError ? failureOf(query.error) : {};
  const stale = isStaleAnswer(failure.status, failure.message);
  useEffect(() => {
    if (stale) {
      void queryClient.invalidateQueries({
        queryKey: festivalPageKey(page.record_id),
      });
    }
  }, [stale, queryClient, page.record_id]);

  const storedLines = mode === "changes" ? page.changes_preview : page.preview;
  const settled =
    askedKey === bodyKey &&
    query.status === "success" &&
    !query.isPlaceholderData;

  // An untouched form is the page as Tina wrote it: Tina's own stored preview is the answer, so nothing is asked.
  if (!asks || !dirty) {
    return {
      lines: storedLines,
      problems: [],
      state: "stored",
      errorText: null,
    };
  }
  if (body === null) {
    return { lines: [], problems: [], state: "blocked", errorText: null };
  }
  if (settled && query.data) {
    return {
      lines: query.data.lines,
      problems: query.data.problems,
      state: "fresh",
      errorText: null,
    };
  }
  if (query.isError && askedKey === bodyKey) {
    return {
      lines: [],
      problems: [],
      state: "error",
      errorText: errorTextOf(failure.status, failure.message),
    };
  }
  if (query.data !== undefined) {
    return {
      lines: query.data.lines,
      problems: [],
      state: "waiting",
      errorText: null,
    };
  }
  return {
    lines: storedLines,
    problems: [],
    state: "waiting",
    errorText: null,
  };
}
